import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  StyleSheet,
  FlatList,
  RefreshControl,
  Animated,
  Dimensions,
} from 'react-native';
import {
  Text,
  Card,
  Button,
  useTheme,
  ActivityIndicator,
  Chip,
  IconButton,
} from 'react-native-paper';
import {
  RefreshCw,
  Filter,
  TrendingUp,
  Clock,
  AlertCircle,
  Wifi,
  WifiOff,
} from 'lucide-react-native';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../../store';
import type {
  ContentRecommendation,
  RecommendationFeedback,
  ContextualFactor,
} from '@coffeeplexity/shared';
import { AIRecommendationCard } from './AIRecommendationCard';
import { RecommendationFilters } from './RecommendationFilters';
import { RecommendationPerformanceTracker } from './RecommendationPerformanceTracker';
import {
  fetchRecommendations,
  refreshRecommendations,
  submitRecommendationFeedback,
  dismissRecommendation,
  acceptRecommendation,
  updateContextualFactors,
  selectActiveRecommendations,
  selectRecommendationLoading,
  selectRecommendationError,
  selectIsRecommendationsCacheStale,
  selectRecommendationsByType,
  selectHighConfidenceRecommendations,
  selectUnviewedRecommendations,
} from '../../store/slices/recommendationEngineSlice';
import { selectCurrentUser } from '../../store/slices/authSlice';
import { useRecommendationAnalytics } from '../../hooks/useRecommendationAnalytics';
import { useNetworkStatus } from '../../hooks/useNetworkStatus';
import { logger } from '../../utils/logger';

interface ContentRecommendationFeedProps {
  contentTypes?: string[];
  maxRecommendations?: number;
  showFilters?: boolean;
  showPerformanceMetrics?: boolean;
  enableRealTimeUpdates?: boolean;
  onRecommendationSelect?: (recommendation: ContentRecommendation) => void;
  onFeedbackSubmitted?: (feedback: RecommendationFeedback) => void;
  style?: any;
}

interface RecommendationFiltersState {
  contentTypes: string[];
  difficultyRange: [number, number];
  confidenceThreshold: number;
  skillCategories: string[];
  timeRange: [number, number];
  sortBy: 'confidence' | 'priority' | 'created_at' | 'difficulty';
  sortOrder: 'asc' | 'desc';
}

const { width: screenWidth } = Dimensions.get('window');

export function ContentRecommendationFeed({
  contentTypes = [],
  maxRecommendations = 20,
  showFilters = true,
  showPerformanceMetrics = false,
  enableRealTimeUpdates = true,
  onRecommendationSelect,
  onFeedbackSubmitted,
  style,
}: ContentRecommendationFeedProps) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isConnected = useNetworkStatus();

  // Redux state
  const currentUser = useSelector(selectCurrentUser);
  const recommendations = useSelector(selectActiveRecommendations);
  const loading = useSelector(selectRecommendationLoading);
  const error = useSelector(selectRecommendationError);
  const isCacheStale = useSelector(selectIsRecommendationsCacheStale);
  const recommendationsByType = useSelector(selectRecommendationsByType);
  const highConfidenceRecommendations = useSelector(
    selectHighConfidenceRecommendations
  );
  const unviewedRecommendations = useSelector(selectUnviewedRecommendations);

  // Local state
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);
  const [filters, setFilters] = useState<RecommendationFiltersState>({
    contentTypes: contentTypes,
    difficultyRange: [0, 1],
    confidenceThreshold: 0.5,
    skillCategories: [],
    timeRange: [5, 120], // 5 minutes to 2 hours
    sortBy: 'priority',
    sortOrder: 'desc',
  });
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState<Date | null>(null);
  const [performanceMetrics, setPerformanceMetrics] = useState({
    loadTime: 0,
    cacheHitRate: 0,
    errorRate: 0,
    userEngagement: 0,
  });

  // Animation values
  const fadeAnim = useState(new Animated.Value(1))[0];
  const slideAnim = useState(new Animated.Value(0))[0];

  // Custom hooks
  const {
    trackRecommendationView,
    trackRecommendationInteraction,
    trackFeedbackSubmission,
    getRecommendationAnalytics,
  } = useRecommendationAnalytics();

  // Filter and sort recommendations
  const filteredRecommendations = useMemo(() => {
    let filtered = recommendations.filter(rec => {
      // Content type filter
      if (
        filters.contentTypes.length > 0 &&
        !filters.contentTypes.includes(rec.contentType)
      ) {
        return false;
      }

      // Difficulty range filter
      if (
        rec.difficultyLevel < filters.difficultyRange[0] ||
        rec.difficultyLevel > filters.difficultyRange[1]
      ) {
        return false;
      }

      // Confidence threshold filter
      if (rec.confidence < filters.confidenceThreshold) {
        return false;
      }

      // Time range filter
      if (
        rec.estimatedMinutes < filters.timeRange[0] ||
        rec.estimatedMinutes > filters.timeRange[1]
      ) {
        return false;
      }

      // Skill categories filter
      if (filters.skillCategories.length > 0) {
        const hasMatchingSkill = rec.skillAlignment.some(skill =>
          filters.skillCategories.includes(skill.skillName)
        );
        if (!hasMatchingSkill) {
          return false;
        }
      }

      return true;
    });

    // Sort recommendations
    filtered.sort((a, b) => {
      let comparison = 0;
      switch (filters.sortBy) {
        case 'confidence':
          comparison = a.confidence - b.confidence;
          break;
        case 'priority':
          comparison = a.priority - b.priority;
          break;
        case 'created_at':
          comparison =
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
        case 'difficulty':
          comparison = a.difficultyLevel - b.difficultyLevel;
          break;
        default:
          comparison = a.priority - b.priority;
      }

      return filters.sortOrder === 'desc' ? -comparison : comparison;
    });

    return filtered.slice(0, maxRecommendations);
  }, [recommendations, filters, maxRecommendations]);

  // Real-time updates effect
  useEffect(() => {
    if (!enableRealTimeUpdates || !currentUser?.id) return;

    const updateInterval = setInterval(() => {
      if (isCacheStale && isConnected) {
        handleRefreshRecommendations(false);
      }
    }, 30000); // Check every 30 seconds

    return () => clearInterval(updateInterval);
  }, [enableRealTimeUpdates, currentUser?.id, isCacheStale, isConnected]);

  // Initial load effect
  useEffect(() => {
    if (currentUser?.id && recommendations.length === 0) {
      handleLoadRecommendations();
    }
  }, [currentUser?.id]);

  // Performance tracking effect
  useEffect(() => {
    const startTime = Date.now();

    if (!loading.recommendations && recommendations.length > 0) {
      const loadTime = Date.now() - startTime;
      setPerformanceMetrics(prev => ({
        ...prev,
        loadTime,
        cacheHitRate: isCacheStale ? 0 : 1,
      }));
      setLastUpdateTime(new Date());
    }
  }, [loading.recommendations, recommendations.length, isCacheStale]);

  // Load recommendations
  const handleLoadRecommendations = useCallback(async () => {
    if (!currentUser?.id) return;

    try {
      const startTime = Date.now();

      await dispatch(
        fetchRecommendations({
          userId: currentUser.id,
          limit: maxRecommendations,
          contentTypes:
            filters.contentTypes.length > 0 ? filters.contentTypes : undefined,
          context: {
            screenWidth,
            isConnected,
            timestamp: new Date().toISOString(),
          },
        })
      ).unwrap();

      const loadTime = Date.now() - startTime;
      setPerformanceMetrics(prev => ({
        ...prev,
        loadTime,
        errorRate: 0,
      }));

      // Update contextual factors
      const contextualFactors: Partial<ContextualFactor>[] = [
        {
          type: 'time_of_day',
          value: new Date().getHours(),
          weight: 0.3,
          lastUpdated: new Date().toISOString(),
        },
        {
          type: 'day_of_week',
          value: new Date().getDay(),
          weight: 0.2,
          lastUpdated: new Date().toISOString(),
        },
        {
          type: 'device_type',
          value: 'mobile',
          weight: 0.1,
          lastUpdated: new Date().toISOString(),
        },
      ];

      dispatch(
        updateContextualFactors({
          userId: currentUser.id,
          factors: contextualFactors,
        })
      );
    } catch (error) {
      logger.error('Failed to load recommendations:', error);
      setPerformanceMetrics(prev => ({
        ...prev,
        errorRate: prev.errorRate + 1,
      }));
    }
  }, [
    currentUser?.id,
    maxRecommendations,
    filters.contentTypes,
    dispatch,
    isConnected,
  ]);

  // Refresh recommendations
  const handleRefreshRecommendations = useCallback(
    async (forceRefresh = true) => {
      if (!currentUser?.id) return;

      setRefreshing(true);
      try {
        await dispatch(
          refreshRecommendations({
            userId: currentUser.id,
            forceRefresh,
          })
        ).unwrap();

        // Animate refresh
        Animated.sequence([
          Animated.timing(fadeAnim, {
            toValue: 0.5,
            duration: 200,
            useNativeDriver: true,
          }),
          Animated.timing(fadeAnim, {
            toValue: 1,
            duration: 200,
            useNativeDriver: true,
          }),
        ]).start();

        setLastUpdateTime(new Date());
      } catch (error) {
        logger.error('Failed to refresh recommendations:', error);
      } finally {
        setRefreshing(false);
      }
    },
    [currentUser?.id, dispatch, fadeAnim]
  );

  // Handle recommendation feedback
  const handleRecommendationFeedback = useCallback(
    async (feedback: RecommendationFeedback) => {
      try {
        await dispatch(submitRecommendationFeedback(feedback)).unwrap();

        // Track feedback for analytics
        trackFeedbackSubmission(feedback);

        // Update performance metrics
        setPerformanceMetrics(prev => ({
          ...prev,
          userEngagement: prev.userEngagement + (feedback.rating || 0) / 5,
        }));

        // Call external callback
        onFeedbackSubmitted?.(feedback);

        // Trigger real-time update if needed
        if (enableRealTimeUpdates) {
          setTimeout(() => {
            handleRefreshRecommendations(false);
          }, 1000);
        }
      } catch (error) {
        logger.error('Failed to submit feedback:', error);
      }
    },
    [
      dispatch,
      trackFeedbackSubmission,
      onFeedbackSubmitted,
      enableRealTimeUpdates,
      handleRefreshRecommendations,
    ]
  );

  // Handle recommendation acceptance
  const handleRecommendationAccept = useCallback(
    (recommendation: ContentRecommendation) => {
      dispatch(
        acceptRecommendation({
          recommendationId: recommendation.id,
          rating: 5,
        })
      );

      trackRecommendationInteraction(recommendation.id, 'accepted', {
        contentType: recommendation.contentType,
        confidence: recommendation.confidence,
      });

      onRecommendationSelect?.(recommendation);
    },
    [dispatch, trackRecommendationInteraction, onRecommendationSelect]
  );

  // Handle recommendation dismissal
  const handleRecommendationDismiss = useCallback(
    (recommendationId: string, reason?: string) => {
      dispatch(
        dismissRecommendation({
          recommendationId,
          reason,
        })
      );

      trackRecommendationInteraction(recommendationId, 'dismissed', {
        reason,
      });
    },
    [dispatch, trackRecommendationInteraction]
  );

  // Handle filter changes
  const handleFiltersChange = useCallback(
    (newFilters: Partial<RecommendationFiltersState>) => {
      setFilters(prev => ({ ...prev, ...newFilters }));

      // Animate filter application
      Animated.timing(slideAnim, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true,
      }).start(() => {
        Animated.timing(slideAnim, {
          toValue: 0,
          duration: 300,
          useNativeDriver: true,
        }).start();
      });
    },
    [slideAnim]
  );

  // Render recommendation item
  const renderRecommendationItem = useCallback(
    ({ item, index }: { item: ContentRecommendation; index: number }) => {
      const analyticsData = getRecommendationAnalytics(item.id);

      return (
        <View style={styles.recommendationItem}>
          <AIRecommendationCard
            recommendation={item}
            onAccept={() => handleRecommendationAccept(item)}
            onDismiss={reason => handleRecommendationDismiss(item.id, reason)}
            onFeedback={handleRecommendationFeedback}
            showAnalytics={showPerformanceMetrics}
            analyticsData={analyticsData}
          />
        </View>
      );
    },
    [
      getRecommendationAnalytics,
      handleRecommendationAccept,
      handleRecommendationDismiss,
      handleRecommendationFeedback,
      showPerformanceMetrics,
    ]
  );

  // Render empty state
  const renderEmptyState = () => (
    <View style={styles.emptyState}>
      <AlertCircle size={48} color={theme.colors.onSurfaceVariant} />
      <Text
        variant='titleMedium'
        style={[styles.emptyTitle, { color: theme.colors.onSurface }]}
      >
        No Recommendations Available
      </Text>
      <Text
        variant='bodyMedium'
        style={[
          styles.emptyDescription,
          { color: theme.colors.onSurfaceVariant },
        ]}
      >
        {!isConnected
          ? 'Connect to the internet to get personalized recommendations'
          : "We're learning about your preferences. Check back soon for personalized content!"}
      </Text>
      {isConnected && (
        <Button
          mode='outlined'
          onPress={() => handleRefreshRecommendations(true)}
          style={styles.retryButton}
          icon={RefreshCw}
        >
          Refresh
        </Button>
      )}
    </View>
  );

  // Render error state
  const renderErrorState = () => (
    <View style={styles.errorState}>
      <AlertCircle size={48} color={theme.colors.error} />
      <Text
        variant='titleMedium'
        style={[styles.errorTitle, { color: theme.colors.error }]}
      >
        Failed to Load Recommendations
      </Text>
      <Text
        variant='bodyMedium'
        style={[
          styles.errorDescription,
          { color: theme.colors.onSurfaceVariant },
        ]}
      >
        {error?.message || 'Something went wrong. Please try again.'}
      </Text>
      <Button
        mode='contained'
        onPress={() => handleRefreshRecommendations(true)}
        style={styles.retryButton}
        icon={RefreshCw}
      >
        Try Again
      </Button>
    </View>
  );

  // Render header
  const renderHeader = () => (
    <View style={styles.header}>
      <View style={styles.headerTop}>
        <View style={styles.headerLeft}>
          <Text
            variant='titleLarge'
            style={[styles.title, { color: theme.colors.onSurface }]}
          >
            AI Recommendations
          </Text>
          {!isConnected && <WifiOff size={16} color={theme.colors.error} />}
          {isConnected && enableRealTimeUpdates && (
            <Chip
              mode='outlined'
              textStyle={styles.liveChipText}
              style={styles.liveChip}
            >
              Live
            </Chip>
          )}
        </View>

        <View style={styles.headerRight}>
          {showFilters && (
            <IconButton
              icon={Filter}
              size={20}
              onPress={() => setShowFiltersPanel(!showFiltersPanel)}
              style={[
                styles.filterButton,
                showFiltersPanel && {
                  backgroundColor: theme.colors.primaryContainer,
                },
              ]}
            />
          )}
          <IconButton
            icon={RefreshCw}
            size={20}
            onPress={() => handleRefreshRecommendations(true)}
            disabled={loading.recommendations}
            style={styles.refreshButton}
          />
        </View>
      </View>

      {lastUpdateTime && (
        <Text
          variant='bodySmall'
          style={[styles.lastUpdate, { color: theme.colors.onSurfaceVariant }]}
        >
          Last updated: {lastUpdateTime.toLocaleTimeString()}
        </Text>
      )}

      {/* Stats chips */}
      <View style={styles.statsContainer}>
        <Chip
          mode='outlined'
          textStyle={styles.statChipText}
          style={styles.statChip}
        >
          {filteredRecommendations.length} recommendations
        </Chip>
        <Chip
          mode='outlined'
          textStyle={styles.statChipText}
          style={styles.statChip}
        >
          {highConfidenceRecommendations.length} high confidence
        </Chip>
        <Chip
          mode='outlined'
          textStyle={styles.statChipText}
          style={styles.statChip}
        >
          {unviewedRecommendations.length} new
        </Chip>
      </View>
    </View>
  );

  if (error && !recommendations.length) {
    return <View style={[styles.container, style]}>{renderErrorState()}</View>;
  }

  return (
    <View style={[styles.container, style]}>
      {/* Performance Tracker */}
      {showPerformanceMetrics && (
        <RecommendationPerformanceTracker
          metrics={performanceMetrics}
          recommendations={filteredRecommendations}
          onOptimizationSuggestion={suggestion => {
            logger.log('Optimization suggestion:', suggestion);
          }}
        />
      )}

      {/* Filters Panel */}
      {showFiltersPanel && showFilters && (
        <RecommendationFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onClose={() => setShowFiltersPanel(false)}
          recommendations={recommendations}
        />
      )}

      {/* Main Content */}
      <Animated.View
        style={{ opacity: fadeAnim, transform: [{ translateY: slideAnim }] }}
      >
        <FlatList
          data={filteredRecommendations}
          renderItem={renderRecommendationItem}
          keyExtractor={item => item.id}
          ListHeaderComponent={renderHeader}
          ListEmptyComponent={
            loading.recommendations ? (
              <View style={styles.loadingState}>
                <ActivityIndicator size='large' />
                <Text
                  variant='bodyMedium'
                  style={[
                    styles.loadingText,
                    { color: theme.colors.onSurfaceVariant },
                  ]}
                >
                  Loading personalized recommendations...
                </Text>
              </View>
            ) : (
              renderEmptyState()
            )
          }
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={() => handleRefreshRecommendations(true)}
              colors={[theme.colors.primary]}
            />
          }
          showsVerticalScrollIndicator={false}
          contentContainerStyle={styles.listContent}
          initialNumToRender={5}
          maxToRenderPerBatch={10}
          windowSize={10}
          removeClippedSubviews={true}
          getItemLayout={(data, index) => ({
            length: 200, // Approximate item height
            offset: 200 * index,
            index,
          })}
        />
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
  },
  headerTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  title: {
    fontWeight: '600',
  },
  liveChip: {
    height: 20,
  },
  liveChipText: {
    fontSize: 9,
    fontWeight: '600',
  },
  filterButton: {
    margin: 0,
  },
  refreshButton: {
    margin: 0,
  },
  lastUpdate: {
    fontSize: 11,
    marginBottom: 8,
  },
  statsContainer: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
  },
  statChip: {
    height: 24,
  },
  statChipText: {
    fontSize: 10,
  },
  listContent: {
    paddingBottom: 20,
  },
  recommendationItem: {
    paddingHorizontal: 16,
    paddingVertical: 4,
  },
  loadingState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
  },
  loadingText: {
    marginTop: 12,
    textAlign: 'center',
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
    paddingHorizontal: 32,
  },
  emptyTitle: {
    marginTop: 16,
    marginBottom: 8,
    textAlign: 'center',
  },
  emptyDescription: {
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 24,
  },
  errorState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
    paddingHorizontal: 32,
  },
  errorTitle: {
    marginTop: 16,
    marginBottom: 8,
    textAlign: 'center',
  },
  errorDescription: {
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 24,
  },
  retryButton: {
    minWidth: 120,
  },
});
